import { useTranslation } from 'react-i18next';
import { TargetTrack } from '../UI';
import { buildMetricView, hasTarget } from '../../services/overviewMetricsService';
import { getYearPlanPotential, type YearPlanMoney } from '../../services/yearPlanPotentialService';
import { formatDkkCompact } from '../../utils/currencyUtils';
import { TargetField } from './TargetField';
import styles from './YearPlanKpi.module.css';

interface YearPlanKpiProps {
  money: YearPlanMoney;
  // The same figures for the last accepted plan, so unaccepted edits can be
  // priced against it.
  serverMoney: YearPlanMoney;
  target: number | null;
  year: number;
  isEditMode: boolean;
  // The target is only editable behind the same write permission and the same
  // lock as the plan itself, so the field appears with edit mode rather than
  // being a separate affordance.
  canEditTarget: boolean;
  onTargetChange: (target: number | null) => void;
}

/**
 * What the year's plan is worth against the year's ceiling, above the Gantt.
 *
 * The overview answers "where does this centre stand"; this answers "is what I am
 * doing right now getting me there". So it is derived from the local plan rather
 * than the server's, and while editing it also prices the unaccepted changes,
 * which is the whole planning loop. Steffen, 2026-08-18: "if I only plan so I
 * have a potential of 10 million, it's not enough, I need to plan more so I can
 * adjust."
 *
 * Shares TargetTrack and the metric view with the overview card, so the same
 * plan reads the same way on both screens.
 */
export const YearPlanKpi = ({
  money,
  serverMoney,
  target,
  year,
  isEditMode,
  canEditTarget,
  onTargetChange
}: YearPlanKpiProps) => {
  const { t, i18n } = useTranslation();
  const format = (value: number): string => formatDkkCompact(value, i18n.language);

  const potential = getYearPlanPotential(money);
  const unsavedDelta = potential - getYearPlanPotential(serverMoney);
  const hasUnsavedDelta = isEditMode && Math.round(unsavedDelta) !== 0;
  const isTargetFieldShown = isEditMode && canEditTarget;

  // Without a ceiling there is nothing to measure against, so the bar is dropped
  // rather than drawn against an invented scale. Null view, not a second return:
  // the field below has to keep its place in ONE tree across the two states,
  // because the very first digit typed into it is what sets the target. Two trees
  // meant the field was a different element before and after that keystroke, so
  // React unmounted it and the planner lost focus mid-figure.
  const metric = {
    target,
    lockedPotential: money.lockedPotential,
    openPotential: money.openPotential,
    realised: money.realised
  };
  const view = hasTarget(metric) ? buildMetricView(metric) : null;

  return (
    <section className={styles.kpi}>
      <div className={styles.header}>
        <span className={styles.label}>
          {t('overview-money')}{' '}
          {view && <span className={styles.unit}>{t('overview-potential')}</span>}
        </span>
        <span className={styles.figure}>
          {format(potential)}
          {view ? (
            <span className={styles.unit}> / {format(view.target)}</span>
          ) : (
            <span className={styles.unit}> {t('overview-potential')}</span>
          )}
        </span>
      </div>
      {view ? (
        <TargetTrack
          lockedPercent={view.lockedPercent}
          openPercent={view.openPercent}
          excessPercent={view.excessPercent}
          targetLabel={t('overview-target-flag')}
          isCompact
          realised={
            view.realised > 0
              ? {
                  percent: view.realisedPercent,
                  label: t('overview-realised-value', { value: format(view.realised) })
                }
              : undefined
          }
        />
      ) : (
        <p className={styles.note}>{t('overview-no-target', { year })}</p>
      )}
      {view && (
        <p className={styles.legend}>
          <span>
            <b>{format(view.lockedPotential)}</b> {t('year-plan-kpi-locked')}
          </span>
          <span>
            <b>{format(view.openPotential)}</b> {t('overview-legend-plannable')}
          </span>
          <span className={view.isOverTarget ? styles.over : undefined}>
            <b>{format(view.isOverTarget ? view.excess : view.roomToTarget)}</b>{' '}
            {view.isOverTarget ? t('overview-legend-excess') : t('overview-legend-room')}
          </span>
        </p>
      )}
      {view && hasUnsavedDelta && (
        <p className={styles.unsaved}>
          {t('year-plan-kpi-unsaved', {
            value: `${unsavedDelta > 0 ? '+' : '−'}${format(Math.abs(unsavedDelta))}`
          })}
        </p>
      )}
      {isTargetFieldShown && <TargetField target={target} onChange={onTargetChange} />}
    </section>
  );
};
